import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Typography, AppButton, ConfirmationModal } from "@libs/zapi-ui/custom";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useProjectStore, TNextStep } from "@/zapi-frontend/stores/project";
import {
  CaretRight,
  DotsThree,
  DownloadSimple,
  Eye,
  ArrowClockwiseIcon,
  Database,
  BookOpen,
  FileText,
  PresentationChart
} from "@phosphor-icons/react";
import { useExportHistory } from "./hooks/useExportHistory";
import { formatTimeAgo } from "@/zapi-frontend/utils/dates";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@libs/zapi-ui/shadcn";
import { VaultDrawer } from "./ui/VaultDrawer";
import { ExportedFilesDrawer } from "./ui/ExportedFilesDrawer";
import { toast } from "sonner";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";

import { scrollToHeader } from "../../lib/scrollToHeader";

type TodoItemProps = {
  todo: TNextStep;
  index: number;
  totalItems: number;
};

function TodoItem({ todo, index, totalItems }: TodoItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const displayText = todo.summary || todo.text;

  return (
    <div className={cn("bg-white", index < totalItems - 1 && "border-b border-innoveraZinc-150")}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full py-2 px-3 flex items-center justify-between gap-2 hover:bg-innoveraZinc-50 transition-colors text-left"
      >
        <Typography variant="label2" color="zinc900" className="text-[14px] leading-[22px] flex-1 font-normal">
          {displayText}
        </Typography>
        <CaretRight
          className={cn("size-5 transition-transform flex-shrink-0", isExpanded && "rotate-90")}
          color="var(--innovera-zinc900)"
          weight="regular"
        />
      </button>
      {isExpanded && todo.summary && (
        <div className="px-3 pb-2">
          <Typography variant="body3" color="zinc600" className="text-[14px] leading-[22px]">
            {todo.text}
          </Typography>
        </div>
      )}
    </div>
  );
}

export function ProjectOverviewIsland() {
  const { selectedProject, regenerateNextSteps, regenerateDecisionStatus } = useProjectStore();
  const { setViewMode, projectChapters, setActiveChapter, setActiveChapterSubsection, sidebarChaptersData } =
    useChapterStore();
  const { closeHandler } = useProjectSidebarStore();
  const { data: exportHistory, isLoading } = useExportHistory(selectedProject?.id.toString() || null, 3);
  const [searchParams, setSearchParams] = useSearchParams();

  const isVaultDrawerOpen = searchParams.get("vault") === "open";
  const openVaultDrawer = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("vault", "open");
      return params;
    });
  };
  const closeVaultDrawer = (open: boolean) => {
    if (!open) {
      window.history.back();
    }
  };
  const [isRegeneratingNextSteps, setIsRegeneratingNextSteps] = useState(false);
  const [isRegeneratingDecisionStatus, setIsRegeneratingDecisionStatus] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isDecisionStatusModalOpen, setIsDecisionStatusModalOpen] = useState(false);
  const [isExportedFilesDrawerOpen, setIsExportedFilesDrawerOpen] = useState(false);
  if (!selectedProject) return null;

  const hasMoreThanThree = (exportHistory?.total || 0) > 3;
  const nextStepsCount = selectedProject.nextSteps?.length || 0;
  const displayedNextSteps = (selectedProject.nextSteps || []).slice(0, 3);

  const handleRegenerateDecisionStatus = async () => {
    if (!selectedProject) return;

    setIsDecisionStatusModalOpen(false);
    setIsRegeneratingDecisionStatus(true);
    try {
      await regenerateDecisionStatus(selectedProject.id);
      toast.success("Decision status regenerated from Executive Summary");
    } catch (error) {
      toast.error("Failed to regenerate decision status. Make sure the Executive Summary chapter has content.");
      console.error("Error regenerating decision status:", error);
    } finally {
      setIsRegeneratingDecisionStatus(false);
    }
  };

  const handleRegenerateNextSteps = async () => {
    if (!selectedProject) return;

    setIsConfirmModalOpen(false);
    setIsRegeneratingNextSteps(true);
    try {
      await regenerateNextSteps(selectedProject.id);
      toast.success("Next steps regenerated from Executive Summary");
    } catch (error) {
      toast.error("Failed to regenerate next steps. Make sure the Executive Summary chapter has content.");
      console.error("Error regenerating next steps:", error);
    } finally {
      setIsRegeneratingNextSteps(false);
    }
  };

  const handlePreview = (fileUrl: string) => {
    window.open(fileUrl, "_blank");
  };

  const handleDownload = async (fileUrl: string, fileName: string) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleInstructionsClick = () => {
    setViewMode("instructions");
    closeHandler();
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("view", "instructions");
        return params;
      },
      { replace: false }
    );
  };

  const handleDocumentClick = () => {
    setSearchParams({ tab: "document" });

    const executiveSummaryChapter = projectChapters.find(
      (chapter) => chapter.projectTemplateChapter.slug === "executive-summary"
    );

    if (!executiveSummaryChapter) return;

    setViewMode("chapter");
    setActiveChapter(executiveSummaryChapter.id.toString());
    closeHandler();

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("chapterId", executiveSummaryChapter.id.toString());
      return params;
    });
  };

  const handlePresentationClick = () => {
    setSearchParams({ tab: "presentation" });
  };

  const handleSeeAllNextSteps = () => {
    const executiveSummaryChapter = projectChapters.find(
      (chapter) => chapter.projectTemplateChapter.slug === "executive-summary"
    );

    if (!executiveSummaryChapter) return;

    const chapterId = executiveSummaryChapter.id.toString();
    const sidebarChapter = sidebarChaptersData.find((c) => c.id === chapterId);
    const nextStepsSubsection = sidebarChapter?.subsections.find((s) => s.toLowerCase().includes("next step"));

    setViewMode("chapter");
    setActiveChapter(chapterId);
    if (nextStepsSubsection) setActiveChapterSubsection(nextStepsSubsection);
    closeHandler();

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("chapterId", chapterId);
      if (nextStepsSubsection) params.set("chapterSubsection", nextStepsSubsection);
      return params;
    });

    setTimeout(() => {
      if (nextStepsSubsection) scrollToHeader(nextStepsSubsection);
    }, 300);
  };

  // useless change to trigger deploy

  return (
    <div className={cn("w-full h-full flex items-start justify-center p-4 pt-4 overflow-y-auto")}>
      <div className="flex flex-col w-[1120px] pb-8 gap-8">
        <div className="grid grid-cols-3 gap-2 items-start">
          <div className="bg-white border border-innoveraZinc-150 px-4 py-3">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between h-6">
                <Typography variant="label3" color="zinc700" className="text-[14px] leading-[13px]">
                  Decision Status
                </Typography>
                <AppButton
                  appearance="contained"
                  size="sm"
                  className="text-[12px] leading-[11px] px-2 py-[5px] h-6"
                  onClick={() => setIsDecisionStatusModalOpen(true)}
                  disabled={isRegeneratingDecisionStatus}
                  startAdornment={
                    <ArrowClockwiseIcon className={`size-4 ${isRegeneratingDecisionStatus ? "animate-spin" : ""}`} />
                  }
                ></AppButton>
              </div>
              <div
                data-testid="overview-decision-status"
                className={cn(
                  "w-full resize-none outline-none overflow-hidden",
                  "text-[14px] leading-[22px] text-innoveraZinc-900",
                  "placeholder:text-innoveraZinc-900",
                  "transition-colors",
                  isRegeneratingDecisionStatus && "opacity-50 cursor-not-allowed"
                )}
                style={{
                  fontFamily: "inherit",
                  minHeight: "22px"
                }}
              >
                {selectedProject.decisionStatus || "No decision status set"}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="border border-innoveraZinc-150 px-4 py-3 flex flex-col gap-2">
              <div className="flex items-center justify-between h-6">
                <div className="flex items-baseline gap-1">
                  <Typography variant="label3" color="zinc700" className="text-[14px] leading-[13px]">
                    Next Steps
                  </Typography>
                </div>
                <div className="flex gap-2">
                  <AppButton
                    appearance="contained"
                    size="sm"
                    className="text-[12px] leading-[11px] px-2 py-[5px] h-6"
                    onClick={() => setIsConfirmModalOpen(true)}
                    disabled={isRegeneratingNextSteps}
                    startAdornment={
                      <ArrowClockwiseIcon className={`size-4 ${isRegeneratingNextSteps ? "animate-spin" : ""}`} />
                    }
                  ></AppButton>{" "}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleSeeAllNextSteps}
                        className="text-innoveraZinc-900 hover:text-innoveraZinc-700 transition-colors text-center pt-1 flex items-center justify-center gap-1"
                      >
                        <Typography variant="body3" color="zinc900" className="text-[12px] leading-[18px]">
                          See All ({nextStepsCount})
                        </Typography>
                        <CaretRight className="size-4" color="var(--innovera-zinc900)" weight="regular" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="flex flex-col gap-1">
                        <Typography variant="body3">Opens the Executive Summary</Typography>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>

              <div className="flex flex-col max-h-[500px] overflow-y-auto custom-thin-scrollbar">
                {displayedNextSteps.map((todo, index) => (
                  <TodoItem key={todo.id} todo={todo} index={index} totalItems={displayedNextSteps.length} />
                ))}
              </div>
            </div>

            <div className="bg-white border border-innoveraZinc-150 px-4 py-3 flex flex-col gap-2">
              <div className="flex items-center justify-between h-5">
                <Typography variant="label2" color="zinc600" className="text-[14px] leading-[13px] font-medium">
                  Exported Files
                </Typography>
                {hasMoreThanThree && (
                  <button
                    onClick={() => setIsExportedFilesDrawerOpen(true)}
                    className="text-innoveraZinc-900 hover:text-innoveraZinc-700 transition-colors flex items-center gap-1"
                  >
                    <Typography variant="body3" color="zinc900" className="text-[12px] leading-[18px]">
                      See All ({exportHistory?.total})
                    </Typography>
                    <CaretRight className="size-4" color="var(--innovera-zinc900)" weight="regular" />
                  </button>
                )}
              </div>

              <div className="flex flex-col">
                {isLoading ? (
                  <div className="py-4 flex items-center justify-center">
                    <Typography variant="body3" color="zinc500" className="text-[12px] leading-[18px]">
                      Loading exports...
                    </Typography>
                  </div>
                ) : !exportHistory?.exports.length ? (
                  <div className="py-4 flex items-center justify-center">
                    <Typography variant="body3" color="zinc500" className="text-[12px] leading-[18px]">
                      No exports yet. Use the Export button to generate reports.
                    </Typography>
                  </div>
                ) : (
                  exportHistory.exports.map((exp, index) => (
                    <div
                      key={exp.id}
                      className={cn(
                        "bg-white py-1 flex items-center justify-between",
                        index < exportHistory.exports.length - 1 && "border-b border-innoveraZinc-150"
                      )}
                    >
                      <div className="flex items-center justify-between flex-1 gap-2 min-w-0">
                        <Typography
                          variant="label2"
                          color="zinc900"
                          className="text-[14px] leading-[18px] font-medium truncate min-w-0"
                          title={exp.fileName}
                        >
                          {exp.fileName}
                        </Typography>
                        <Typography variant="body3" color="zinc600" className="text-[12px] leading-[18px] shrink-0">
                          {formatTimeAgo(exp.createdAt)}
                        </Typography>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <AppButton appearance="text" size="icon" className="size-6 p-0 ml-3">
                            <DotsThree className="size-6" color="var(--innovera-zinc900)" />
                          </AppButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[160px]">
                          <DropdownMenuItem
                            onClick={() => handlePreview(exp.fileUrl)}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <Eye className="size-4" weight="regular" />
                            <Typography variant="label2">Preview</Typography>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDownload(exp.fileUrl, exp.fileName)}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <DownloadSimple className="size-4" weight="regular" />
                            <Typography variant="label2">Download</Typography>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="bg-white border border-innoveraZinc-150">
              <div className="px-4 py-3">
                <Typography variant="label3" color="zinc600" className="text-[14px] leading-[13px]">
                  Project Inputs
                </Typography>
              </div>
              <div className="flex flex-col">
                <button
                  className="flex items-center justify-between px-4 py-3 border-b border-innoveraZinc-150 hover:bg-innoveraZinc-50 transition-colors cursor-pointer w-full text-left"
                  onClick={openVaultDrawer}
                >
                  <div className="flex items-center gap-3">
                    <Database className="size-5" color="var(--innovera-zinc900)" weight="regular" />
                    <Typography variant="label2" color="zinc900" className="text-[14px] leading-[20px]">
                      Vault
                    </Typography>
                  </div>
                  <CaretRight className="size-5" color="var(--innovera-zinc900)" weight="regular" />
                </button>
                <button
                  className="flex items-center justify-between px-4 py-3 hover:bg-innoveraZinc-50 transition-colors cursor-pointer w-full text-left"
                  onClick={handleInstructionsClick}
                >
                  <div className="flex items-center gap-3">
                    <BookOpen className="size-5" color="var(--innovera-zinc900)" weight="regular" />
                    <Typography variant="label2" color="zinc900" className="text-[14px] leading-[20px]">
                      Project Instructions
                    </Typography>
                  </div>
                  <CaretRight className="size-5" color="var(--innovera-zinc900)" weight="regular" />
                </button>
              </div>
            </div>

            <div className="bg-white border border-innoveraZinc-150">
              <div className="px-4 py-3">
                <Typography variant="label3" color="zinc600" className="text-[14px] leading-[13px]">
                  View Project
                </Typography>
              </div>
              <div className="flex flex-col">
                <button
                  className="flex items-center justify-between px-4 py-3 hover:bg-innoveraZinc-50 transition-colors cursor-pointer w-full text-left border-b border-innoveraZinc-150"
                  onClick={handleDocumentClick}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="size-5" color="var(--innovera-zinc900)" weight="regular" />
                    <Typography variant="label2" color="zinc900" className="text-[14px] leading-[20px]">
                      Document
                    </Typography>
                  </div>
                  <CaretRight className="size-5" color="var(--innovera-zinc900)" weight="regular" />
                </button>
                <button
                  className="flex items-center justify-between px-4 py-3 hover:bg-innoveraZinc-50 transition-colors cursor-pointer w-full text-left"
                  onClick={handlePresentationClick}
                >
                  <div className="flex items-center gap-3">
                    <PresentationChart className="size-5" color="var(--innovera-zinc900)" weight="regular" />
                    <Typography variant="label2" color="zinc900" className="text-[14px] leading-[20px]">
                      Presentation
                    </Typography>
                  </div>
                  <CaretRight className="size-5" color="var(--innovera-zinc900)" weight="regular" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleRegenerateNextSteps}
        title="Regenerate Next Steps"
        description="This action will delete all existing next steps and generate new ones from the Executive Summary. This cannot be undone. Are you sure you want to continue?"
        confirmText="Yes, Regenerate"
        cancelText="Cancel"
        confirmButtonColor="zinc950"
      />

      <ConfirmationModal
        isOpen={isDecisionStatusModalOpen}
        onClose={() => setIsDecisionStatusModalOpen(false)}
        onConfirm={handleRegenerateDecisionStatus}
        title="Regenerate Decision Status"
        description="This action will overwrite the current decision status with AI-generated content. This cannot be undone. Are you sure you want to continue?"
        confirmText="Yes, Regenerate"
        cancelText="Cancel"
        confirmButtonColor="zinc950"
      />

      <VaultDrawer
        open={isVaultDrawerOpen}
        onOpenChange={closeVaultDrawer}
        projectId={selectedProject.id.toString()}
      />

      <ExportedFilesDrawer
        open={isExportedFilesDrawerOpen}
        onOpenChange={setIsExportedFilesDrawerOpen}
        projectId={selectedProject.id.toString()}
      />

    </div>
  );
}
